<template>
  <pf-data-list expandable>
    <pf-data-list-item v-for="row in offerte" :key="row.title">
      <pf-data-list-cell data-ouia-component-id="offers-title">
        <template v-if="row.titolo_offerta">
          <pf-title h="4">{{ row.titolo_offerta }}</pf-title>
          <p>{{ $t('Offerta nr. {0}', [row.id])}}</p>
        </template>
        <pf-title h="4" v-else>{{ $t('Offerta nr. {0}', [row.id]) }}</pf-title>
      </pf-data-list-cell>

      <pf-data-list-cell data-ouia-component-id="offers-date">
        <pf-title h="4">{{ $t('Data offerta') }}</pf-title>
        {{ $date(row.data) }}
      </pf-data-list-cell>

      <pf-data-list-cell data-ouia-component-id="offers-customer">
        <pf-title h="4">{{ row.stato_offerta ? $t('Offerta intestata a') : $t('Ordine intestato a') }}</pf-title>
        <p>{{ row.ragione_sociale }}</p>
      </pf-data-list-cell>

      <pf-data-list-cell data-ouia-component-id="offers-amount">
        <pf-title h="4">{{ $t('Importo') }}</pf-title>
        <p>{{ $currency(row.gran_totale, row.currency) }} <span class="small" v-if="row.gran_totale_ivato - row.gran_totale > 0">+ {{ $t('IVA') }}</span></p>
      </pf-data-list-cell>

      <pf-data-list-action class="pf-v6-u-flex-direction-column pf-v6-u-flex-direction-row-on-md">
        <pf-button variant="tertiary" small target="_blank" :href="`/rest/offer-pdf/${row.id}`" :title="$t('Scarica PDF')">
          <template #icon><file-pdf-icon /></template>
        </pf-button>

        <pf-button
          v-if="row.stato_offerta && row.stato_offerta != 'rifiutata'"
          :disabled="!row.enable_orders"
          variant="danger"
          small
          @click="rejectedId = row.id"
          :title="$t('Rifiuta offerta')"
        >
          <template #icon><ban-icon /></template>
          <form-help-tooltip v-if="!row.enable_orders" :text="$t('Non hai i permessi per rifiutare quest\'offerta. Richiedili ad un utente amministratore dell\'azienda intestataria.')" />
        </pf-button>

        <granted-link
          v-if="row.stato_ordine != 'concluso' && row.stato_offerta != 'rifiutata'"
          :force-disabled="!row.enable_orders"
          :to="{ name: 'confirm_offer', params: { id: row.id } }"
          :disabled-message="$t('Non hai i permessi per confermare l\'offerta. Richiedili ad un utente amministratore dell\'azienda intestataria.')"
          type="primary"
          small
          :title="$t('Conferma ordine')"
        >
          <template #icon><check-icon /></template>
          <span class="pf-v6-u-display-none pf-v6-u-display-inline-on-md">{{ $t('Conferma ordine') }}</span>
        </granted-link>
      </pf-data-list-action>

      <pf-data-list-content>
        <offer-detail :offerta="row" />

        <pf-data-list-action class="pf-v6-u-flex-direction-column pf-v6-u-flex-direction-row-on-sm pf-v6-u-mt-md">
          <pf-button variant="tertiary" target="_blank" :href="`/rest/offer-pdf/${row.id}`" :title="$t('Scarica PDF')">
            <template #icon><file-pdf-icon /></template>
            {{ $t('Scarica PDF') }}
          </pf-button>

          <pf-button
            v-if="row.stato_offerta && row.stato_offerta != 'rifiutata'"
            :disabled="!row.enable_orders"
            variant="danger"
            @click="rejectedId = row.id"
            :title="$t('Rifiuta offerta')"
          >
            <template #icon><ban-icon /></template>
            {{ $t('Rifiuta offerta') }}
            <form-help-tooltip v-if="!row.enable_orders" :text="$t('Non hai i permessi per rifiutare quest\'offerta. Richiedili ad un utente amministratore dell\'azienda intestataria.')" />
          </pf-button>

          <granted-link
            v-if="row.stato_ordine != 'concluso' && row.stato_offerta != 'rifiutata'"
            :force-disabled="!row.enable_orders"
            :to="{ name: 'confirm_offer', params: { id: row.id } }"
            :disabled-message="$t('Non hai i permessi per confermare l\'offerta. Richiedili ad un utente amministratore dell\'azienda intestataria.')"
            type="primary"
            :title="$t('Conferma ordine')"
          >
            <template #icon><check-icon /></template>
            {{ $t('Conferma ordine') }}
          </granted-link>
        </pf-data-list-action>
      </pf-data-list-content>
    </pf-data-list-item>
  </pf-data-list>

  <modal-form
    v-if="rejectedId"
    :title="$t('Rifiuta l\'offerta')"
    variant="small"
    :submit-text="$t('Rifiuta')"
    @submit="submit"
    @close="rejectedId = undefined"
  >
    <form-group :name="$t('Motivazione rifiuto')" required>
      <pf-form-select v-model="motivazioneRifiuto" name="motivazione_rifiuto" required>
        <pf-form-select-option value="prezzo">{{ $t('Prezzo superiore alle aspettative') }}</pf-form-select-option>
        <pf-form-select-option value="altro fornitore">{{ $t('Ho scelto un altro fornitore') }}</pf-form-select-option>
        <pf-form-select-option value="tempistiche">{{ $t('Tempistiche di risposta commerciali non adeguate') }}</pf-form-select-option>
        <pf-form-select-option value="non più necessario">{{ $t('I prodotti richiesti non sono più necessari') }}</pf-form-select-option>
        <pf-form-select-option value="altro">{{ $t('Altro') }}</pf-form-select-option>
      </pf-form-select>
    </form-group>
  </modal-form>
</template>

<style>
.small {
  font-size: var(--pf-v6-c-content--small--FontSize);
  color: var(--pf-v6-c-content--small--Color);
}
</style>

<script lang="ts" setup>
import {ref, type Ref } from 'vue';
import type { Offer } from '@/resources';
import { OffersResource } from '@/resources';

import OfferDetail from './offer-detail.vue';
import GrantedLink from './granted-link.vue';
import FormHelpTooltip from '@/components/FormHelpTooltip.vue';
import FilePdfIcon from '@vue-patternfly/icons/file-pdf-icon';
import BanIcon from '@vue-patternfly/icons/ban-icon';
import CheckIcon from '@vue-patternfly/icons/check-icon';

defineProps<{
  offerte: Offer[];
  loading?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:loading', value: boolean): void
  (e: 'change-state'): void
}>();

type MotivazioniRifiuto = 'prezzo' | 'altro fornitore' | 'tempistiche' | 'non più necessario' | 'altro';

const rejectedId: Ref<number | undefined> = ref();
const motivazioneRifiuto: Ref<MotivazioniRifiuto | undefined> = ref();

async function submit() {
  if (rejectedId.value && motivazioneRifiuto.value) {
    emit('update:loading', true);
    await new OffersResource().put(rejectedId.value, { stato: 'rifiutata', motivazione_rifiuto: motivazioneRifiuto.value });

    rejectedId.value = undefined;
    emit('update:loading', false);
    emit('change-state');
  } else {
    alert(motivazioneRifiuto.value);
  }
}
</script>
